import { CoreImageView } from '@core/widgets';
import { useBehavior } from '@libs/core';
import { TWidgetProps } from '@libs/types';
import clsx from 'clsx';
import React, { Activity } from 'react';
import { TStatusTrackerData } from '../../types/data';
import { StatusTrackerEnum } from '../../types/enums';
import { getInlineStyles } from '../../utils/styles';
import { getHref } from '../Image/imageUtils';
import { getIconColor } from '../Image/VisualView';
import { TextCommon } from '../UI/Text/TextCommon';
import styles from './styles/statusTracker.module.css';

export const StatusTracker = ({ widget }: TWidgetProps<TStatusTrackerData>) => {
    const { isHidden, isDisabled } = useBehavior(widget);

    const getStatusIcon = (status: StatusTrackerEnum) => {
        let iconName = '';
        const iconHierarchy = status === StatusTrackerEnum.WARNING || status === StatusTrackerEnum.FAILED ? 'destructive' : 'primary';

        if (status === StatusTrackerEnum.WAITING) {
            iconName = 'ic_wait_fill';
        } else if (status === StatusTrackerEnum.SUCCESS) {
            iconName = 'ic_checkmark_fill';
        } else if (status === StatusTrackerEnum.WARNING) {
            iconName = 'ic_info_fill';
        } else if (status === StatusTrackerEnum.FAILED) {
            iconName = 'ic_x_fill';
        }

        if (iconName) {
            return (
                <span data-test={`${widget.uid}_icon`} className={styles.iconWrapper}>
                    <CoreImageView
                        src={getHref(iconName)}
                        styleProps={{
                            iconColor: `var(--color-${getIconColor(isDisabled, iconHierarchy)})`,
                        }}
                    />
                </span>
            );
        }
        return null;
    };

    const hasStatusWithIcon = widget.data?.items?.find((item) => getStatusIcon(item?.state) !== null);

    if (Array.isArray(widget.data?.items) && widget.data?.items.length > 0) {
        return (
            <Activity mode={isHidden ? 'hidden' : 'visible'}>
                <div
                    data-test={widget.uid}
                    style={getInlineStyles(widget.options)}
                    className={clsx(styles.wrapper, hasStatusWithIcon && styles.wrapperWithIcon)}
                >
                    {widget.data.items.map((item, index) => (
                        <div key={`${item.state}-${index}`} data-test={`${widget.uid}_item`} className={clsx(styles.item, styles[item.state])}>
                            {getStatusIcon(item.state)}
                            <div className={styles.itemContent}>
                                {item.title && (
                                    <TextCommon
                                        isWidget={false}
                                        as="p"
                                        uid={`${widget.uid}_title`}
                                        content={item.title}
                                        options={{ variant: 'main', textColor: item.state === StatusTrackerEnum.INITIAL ? 'grey_72_lc' : undefined }}
                                    />
                                )}
                                {item.subtitle && (
                                    <TextCommon
                                        isWidget={false}
                                        uid={`${widget.uid}_subtitle`}
                                        as="p"
                                        content={item.subtitle}
                                        options={{ variant: 'secondary', textColor: 'grey_40_lc', margin: { top: 4 } }}
                                    />
                                )}
                                {Array.isArray(item.descriptionLines) && item.descriptionLines.length > 0 && (
                                    <div data-test={`${widget.uid}_descriptions`} className={styles.descriptions}>
                                        {item.descriptionLines.map((description) => (
                                            <TextCommon
                                                key={`description-${index}`}
                                                isWidget={false}
                                                as="p"
                                                content={description}
                                                options={{ variant: 'secondary', textColor: 'grey_72_lc' }}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </Activity>
        );
    }
    return false;
};
